import type {
  FeatureFlagKey,
  FeatureFlagService,
} from "../services/feature-flags";

export interface ModuleContentView {
  readonly body: string;
  readonly heading: string;
  readonly kind: "content";
  readonly mount?: (container: HTMLElement) => void;
}

export interface ModuleErrorView {
  readonly body: string;
  readonly heading: string;
  readonly kind: "error-card";
}

export type ModuleView = ModuleContentView | ModuleErrorView;

export interface TrainingModule {
  readonly featureFlag: FeatureFlagKey;
  readonly id: string;
  readonly title: string;
  render(): ModuleContentView;
}

/**
 * The view for a module, or **null** where its flag is off.
 *
 * Null, not a card saying the module is unavailable. Hard rule 7, as ruled on
 * 17 October 2026 (a) section 11 item 5: nothing of a feature whose flag is
 * false reaches the screen. A card headed "Battle Procedure Runner — this
 * module is not enabled in this build" puts the feature's name, its existence
 * and its state on the trainee's screen, which is three things more than
 * nothing.
 *
 * It was written the other way on purpose, to tell a disabled feature apart
 * from a broken one. That reasoning holds for a control inside a feature he is
 * using; it does not hold for a whole module he has no business seeing, and
 * the flag snapshot in the bug report answers the same question without
 * putting anything on screen.
 */
export function resolveModuleView(
  module: TrainingModule,
  flags: FeatureFlagService,
): ModuleView | null {
  if (!flags.isEnabled(module.featureFlag)) return null;

  try {
    return module.render();
  } catch (error: unknown) {
    return {
      kind: "error-card",
      heading: `${module.title} could not start`,
      body: error instanceof Error ? error.message : "Unknown module failure.",
    };
  }
}
